import { router } from '@inertiajs/react';
import { NotificationBanner } from '@/components/ui/notification-banner';
import React from 'react';

interface AuthStatusContextValue {
    isAuthenticated: boolean | null; // null until first check
    forciblyLoggedOut: boolean;
    lastCheckedAt: number | null;
    triggerCheck: () => Promise<void>;
}

const AuthStatusContext = React.createContext<
    AuthStatusContextValue | undefined
>(undefined);

let redirecting = false;

export const AuthStatusProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState<
        boolean | null
    >(null);
    const [forciblyLoggedOut, setForciblyLoggedOut] = React.useState(false);
    const [lastCheckedAt, setLastCheckedAt] = React.useState<number | null>(
        null,
    );
    const hasEverAuthenticated = React.useRef(false);

    const triggerCheck = React.useCallback(async () => {
        try {
            const res = await fetch('/auth/ping', {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json',
                },
            });
            setLastCheckedAt(Date.now());
            let authenticated: boolean | null = null;
            const contentType = res.headers.get('content-type') ?? '';
            if (contentType.includes('application/json')) {
                try {
                    const data = await res.json();
                    authenticated =
                        typeof data?.authenticated === 'boolean'
                            ? data.authenticated
                            : null;
                } catch {
                    authenticated = null;
                }
            }

            if (authenticated === true) {
                setIsAuthenticated(true);
                hasEverAuthenticated.current = true;
                return;
            }

            if (authenticated === false) {
                // Determine current location context
                const path =
                    typeof window !== 'undefined'
                        ? window.location.pathname
                        : '';
                const isAuthPage =
                    /^\/(login|register|password|two-factor)(\/|$)/.test(path);
                const isProtectedPage = /^\/(user|admin)(\/|$)/.test(path);

                setIsAuthenticated(false);
                const hadSession = hasEverAuthenticated.current && !isAuthPage;
                let userInitiatedLogout = false;

                try {
                    userInitiatedLogout =
                        localStorage.getItem('ftob:logout_intent') === '1';
                    if (userInitiatedLogout) {
                        localStorage.removeItem('ftob:logout_intent');
                    }
                } catch {
                    userInitiatedLogout = false;
                }

                // Mark that the user has been logged out due to inactivity so any page can show feedback.
                if (hadSession && !userInitiatedLogout) {
                    let noticeShown = false;
                    try {
                        noticeShown =
                            sessionStorage.getItem(
                                'ftob:logout_notice_shown',
                            ) === '1';
                    } catch {
                        noticeShown = false;
                    }

                    if (!noticeShown) {
                        try {
                            localStorage.setItem(
                                'ftob:logout_reason',
                                'inactive',
                            );
                            sessionStorage.setItem(
                                'ftob:logout_notice_shown',
                                '1',
                            );
                        } catch {}
                        setForciblyLoggedOut(true);
                    }
                }

                // If the user had a session and is currently on a protected page, go straight to login.
                // Do NOT bounce through home. On public pages, don't auto-redirect at all.
                if (hadSession && isProtectedPage && !redirecting) {
                    redirecting = true;
                    router.visit('/login', { replace: true });
                    setTimeout(() => {
                        redirecting = false;
                    }, 1000);
                }
            }
        } catch {
            // Network issues: leave auth state unchanged
        }
    }, []);

    // Initial check
    React.useEffect(() => {
        triggerCheck();
    }, [triggerCheck]);

    // Focus & visibility listeners
    React.useEffect(() => {
        const onFocus = () => triggerCheck();
        const onVis = () => {
            if (document.visibilityState === 'visible') triggerCheck();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [triggerCheck]);

    // Fallback: when the user interacts after being idle for a while,
    // run a quick auth check so session expiry is detected immediately.
    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const onUserInteraction = () => {
            const now = Date.now();
            const last = lastCheckedAt ?? 0;
            const staleAfterMs = 60_000; // only re-check if last ping is older than 60s

            if (now - last > staleAfterMs) {
                void triggerCheck();
            }
        };

        window.addEventListener('click', onUserInteraction, true);

        return () => {
            window.removeEventListener('click', onUserInteraction, true);
        };
    }, [lastCheckedAt, triggerCheck]);

    const value: AuthStatusContextValue = {
        isAuthenticated,
        forciblyLoggedOut,
        lastCheckedAt,
        triggerCheck,
    };

    const shouldShowGlobalNotice =
        forciblyLoggedOut &&
        typeof window !== 'undefined' &&
        !/^\/(login|register|password|two-factor)(\/|$)/.test(
            window.location.pathname,
        );

    return (
        <AuthStatusContext.Provider value={value}>
            <>
                {shouldShowGlobalNotice && (
                    <NotificationBanner
                        message="Your session expired due to inactivity. Please log in again."
                        type="warning"
                        onDismiss={() => {
                            setForciblyLoggedOut(false);
                        }}
                        autoHideMs={8000}
                    />
                )}
                {children}
            </>
        </AuthStatusContext.Provider>
    );
};

export function useAuthStatus(): AuthStatusContextValue {
    const ctx = React.useContext(AuthStatusContext);
    if (!ctx)
        throw new Error('useAuthStatus must be used within AuthStatusProvider');
    return ctx;
}
